import { CClient, showErrorAlert } from '@common/utils'
import Spinner from '@components/common/spinner'
import QuizCard from '@components/quizzes/quiz-card'
import { CompetititonsListType } from '@lib/types/competitions'
import { QuizLinkedEntityType } from '@lib/types/quizzes'
import { getAPIErrorString } from '@lib/utils/common'
import { captureException } from '@sentry/node'
import { useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

type Props = {
  quizzes: CompetititonsListType<QuizLinkedEntityType>
  nextPage?: string
}

const QuizList = ({
  quizzes: initialQuizzes,
  nextPage: initialNextPage,
}: Props) => {
  const [quizzes, setQuizzes] = useState<
    CompetititonsListType<QuizLinkedEntityType>
  >(initialQuizzes)
  const [nextPage, setNextPage] = useState(initialNextPage)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const handleLoadMore = async () => {
    if (isLoading || !nextPage) return
    setIsLoading(true)

    try {
      const response = await CClient(nextPage)
      if (!response.ok)
        throw new Error(await getAPIErrorString(response, nextPage))
      const data = await response.json()
      setNextPage(data?.next)
      const results = data?.results
      setQuizzes((prevItems) => [...prevItems, ...results])
    } catch (error) {
      setHasError(true)
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong. Please refresh the page and try again or contact support for help with this issue.',
      })
    }
    setIsLoading(false)
  }

  const infiniteRef = useInfiniteScroll<HTMLDivElement>({
    loading: isLoading,
    hasNextPage: !hasError && nextPage !== null,
    onLoadMore: handleLoadMore,
  })

  return (
    <>
      <div
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10"
        ref={infiniteRef}
      >
        {quizzes.map((quiz) => (
          <QuizCard quiz={quiz} key={quiz.id} />
        ))}
      </div>
      {isLoading && (
        <div className="flex justify-center py-20 w-full">
          <Spinner classes="text-orange h-10 w-10" />
        </div>
      )}
    </>
  )
}

export default QuizList
